import {gql} from '@apollo/client';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {PermissionIntentFilter} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {Optional} from 'util/Types';

import {
  TemplateCollectionSelectorQuery,
  TemplateCollectionSelectorQueryVariables,
} from './__generated/TemplateCollectionSelectorQuery';
type TemplateCollectionGraphQL =
  TemplateCollectionSelectorQuery['templateCollectionsListing']['collections'][number];

function collectionId(collectionValue: Optional<TemplateCollectionGraphQL | string>): string | null {
  if (collectionValue == null) {
    return null;
  }
  if (typeof collectionValue === 'string') {
    return collectionValue;
  }
  return collectionValue.id;
}

interface CollectionOption {
  id: string;
  text: string;
  collection: TemplateCollectionGraphQL;
}

function collectionOpt(collection: TemplateCollectionGraphQL): CollectionOption {
  return {
    id: collectionId(collection)!,
    text: collection.name,
    collection,
  };
}

interface QueryingTemplateCollectionSelectorProps {
  /**
   * value is either a TemplateCollection or TemplateCollection id (string)
   */
  value: Optional<TemplateCollectionGraphQL | string>;
  onChange: (templateCollection: TemplateCollectionGraphQL) => void;
  disabled?: boolean;
  searchable?: boolean;
  includeOnlyOrgOwnedTemplateCollections?: boolean;
  allowedOwnerIds?: Array<string>;
}

interface TemplateCollectionsSelectorProps extends QueryingTemplateCollectionSelectorProps {
  collections: Array<TemplateCollectionGraphQL> | null;
}

/**
 * @package exported for testing
 */
export function TemplateCollectionSelectorDisplay({
  value,
  onChange,
  collections,
  disabled,
  searchable,
}: TemplateCollectionsSelectorProps): JSX.Element {
  const options = useMemo(
    () => collections?.map((collection) => collectionOpt(collection)) ?? [],
    [collections]
  );

  const collectionIdToCollectionOption = useMemo(
    () =>
      collections?.reduce(
        (acc, collection) => {
          acc[collection.id] = collectionOpt(collection);
          return acc;
        },
        {} as Record<string, CollectionOption>
      ) ?? {},
    [collections]
  );

  const changeTemplateCollectionHandler = (e: CollectionOption): void => {
    assert(onChange != null, 'must provide onChange');
    onChange(e.collection);
  };

  let valueText = 'Choose collection...';
  if (value != null) {
    if (typeof value === 'string') {
      const collection = collections?.find((c) => c.id === value);
      if (collection) {
        valueText = collection.name;
      }
    } else if (value.name) {
      valueText = value.name;
    }
  }

  return (
    <SelectDropdown
      displayName={valueText}
      disabled={disabled || collections == null}
      menu={
        searchable ? (
          <SearchableMenu
            selectedValue={collectionId(value)}
            onSelect={(id) => {
              changeTemplateCollectionHandler(collectionIdToCollectionOption[id]);
            }}
            optionDatas={options.map((option) => ({key: option.id, value: option.id, name: option.text}))}
            width={Menu.WIDTHS.FULL}
          />
        ) : (
          <Menu value={collectionId(value)} style={{overflowY: 'auto'}} width={Menu.WIDTHS.FULL}>
            {options.map((option) => (
              <Option
                key={option.id}
                value={option.id}
                onSelect={() => {
                  changeTemplateCollectionHandler(option);
                }}
              >
                {option.text}
              </Option>
            ))}
          </Menu>
        )
      }
      dataTestAttrs={{component: 'TemplateCollectionSelector', element: 'root'}}
    />
  );
}

export const TEMPLATE_COLLECTION_SELECTOR_QUERY = gql`
  query TemplateCollectionSelectorQuery(
    $searchInput: TemplateCollectionListingSearchInput
    $limitOffsetInput: LimitOffsetInput
  ) @skipAudit @engComponent(name: NOTEBOOK) {
    templateCollectionsListing(searchInput: $searchInput, limitOffsetInput: $limitOffsetInput) {
      collections {
        ...TemplateCollectionGraphQL
      }
      total
    }
  }
  ${getFragmentWrapper('TemplateCollection').fragment}
`;

export default function QueryingTemplateCollectionSelector(
  props: QueryingTemplateCollectionSelectorProps
): JSX.Element {
  const result = useBenchlingQuery<TemplateCollectionSelectorQuery, TemplateCollectionSelectorQueryVariables>(
    TEMPLATE_COLLECTION_SELECTOR_QUERY,
    {
      variables: {
        searchInput: {
          permissionsLevel: PermissionIntentFilter.WRITE,
          onlyOrgOwned: props.includeOnlyOrgOwnedTemplateCollections ?? false,
          allowedOwnerIds: props.allowedOwnerIds,
        },
      },
    }
  );

  const collections =
    result.type === QueryResultType.SUCCESS ? result.data.templateCollectionsListing.collections : null;
  return <TemplateCollectionSelectorDisplay {...props} collections={collections} />;
}
